import {useEffect, useMemo, useState} from 'react';
import {graphql, forecastFetchQuery} from '../../../graphql/forecastRelay';
import {UNASSIGNED_KEY} from '../utils/groupTickets';
import type {DashboardFilterState} from './useDashboardFilterPanel';
import type {DashboardDateField} from './useDashboardFilters';
import type {DashboardGroupByToken, TicketGroupStats} from '../utils/groupTickets';
import type {DashboardTotals} from '../types/dashboardTicket';
import type {
	TicketDashboardRollupsInput,
	TicketRollupGroupBy,
	useDashboardRollupsQuery$data,
} from './__generated__/useDashboardRollupsQuery.graphql';

/**
 * The dashboard's group-by tokens that are also rollup dimensions.
 *
 * 'none' means no grouping, and 'project' is not a rollup dimension at all —
 * project association is not a ticket-list filter, so the aggregate cannot be
 * bucketed by it. Neither may be sent: an unrecognised value degrades to no
 * grouping, which would answer with ungrouped sub-totals rather than an error.
 */
const ROLLUP_GROUP_BY: Partial<Record<DashboardGroupByToken, TicketRollupGroupBy>> = {
	status: 'status',
	priority: 'priority',
	assignee: 'assignee',
	channel: 'channel',
	requester: 'requester',
};

/** The rollup dimension a group-by token maps to, or undefined where it has none. */
export function rollupGroupByFor(token: DashboardGroupByToken): TicketRollupGroupBy | undefined {
	return ROLLUP_GROUP_BY[token];
}

const DashboardRollupsQuery = graphql`
	query useDashboardRollupsQuery($rollupInput: TicketDashboardRollupsInput!) {
		viewer {
			ticketDashboardRollups(input: $rollupInput) {
				overall {
					total
					resolvedCount
					pctResolved
					medianResolutionTimeSeconds
					medianFirstResponseTimeSeconds
					slaAchievementRate
				}
				groups {
					key
					rollup {
						total
						resolvedCount
						pctResolved
						medianResolutionTimeSeconds
						medianFirstResponseTimeSeconds
						slaAchievementRate
					}
				}
			}
		}
	}
`;

/**
 * Builds the rollup input from the same filter state the ticket query is built
 * from, so the totals row and the rows below it always summarise the same
 * filtered set. Pagination is deliberately absent — the aggregate covers the
 * whole set, not a page — and so are sort/order, which cannot change a total.
 *
 * `includeUnassigned` is omitted because the filter panel has no unassigned
 * toggle to drive it, matching the ticket query.
 */
export function buildDashboardRollupsInput(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField,
	groupBy: DashboardGroupByToken
): TicketDashboardRollupsInput {
	const input: Record<string, unknown> = {};

	if (applied.status.length > 0) input.status = applied.status;
	if (applied.priority.length > 0) input.priority = applied.priority;
	if (applied.channel.length > 0) input.channel = applied.channel;
	if (applied.assignee.length > 0) input.assigneeId = applied.assignee;
	if (applied.requester.length > 0) input.requesterId = applied.requester;
	if (applied.tags.length > 0) input.labelId = applied.tags;
	if (applied.slaState.length > 0) input.slaStatus = applied.slaState;

	if (dateField === 'last_activity_at') {
		input.updatedAfter = from;
		input.updatedBefore = to;
	} else {
		// 'created_at' maps directly; 'resolved_at' falls back to the created-date
		// range because no resolved-date range input exists yet. The ticket query
		// makes the same approximation, so the totals row and the table stay
		// consistent with each other even while both are approximate.
		input.createdAfter = from;
		input.createdBefore = to;
	}

	const rollupGroupBy = rollupGroupByFor(groupBy);
	if (rollupGroupBy !== undefined) {
		input.groupBy = rollupGroupBy;
	}

	// The filter panel holds its selections as plain strings (they are option ids
	// from the server), so the enum-typed members are narrowed once, here, rather
	// than value-by-value: an unrecognised value is the server's to reject.
	return input as TicketDashboardRollupsInput;
}

/** The shape of `ticketDashboardRollups` in the query response. */
export type RollupsServerPayload = NonNullable<NonNullable<useDashboardRollupsQuery$data['viewer']>['ticketDashboardRollups']>;

/** The mapped output consumed by the totals row and the group headers. */
export interface DashboardRollupsResult {
	overall: DashboardTotals;
	groupStats: Map<string, TicketGroupStats> | null;
}

function rollupToTotals(r: RollupsServerPayload['overall']): DashboardTotals {
	return {
		total: r.total,
		pct_resolved: r.pctResolved ?? null,
		sla_achievement_rate: r.slaAchievementRate ?? null,
		median_resolution_time_seconds: r.medianResolutionTimeSeconds ?? null,
		median_first_response_time_seconds: r.medianFirstResponseTimeSeconds ?? null,
	};
}

/**
 * Translates a server bucket key into the key vocabulary `groupTickets` buckets
 * the loaded rows under, so a sub-total lands on the header it belongs to.
 *
 * Assignee is the only dimension the server can leave empty, and it uses `''`
 * where the client uses its own sentinel. Every other dimension keys on the same
 * string on both sides: status / priority / channel are the raw enum values, and
 * the assignee and requester keys are the raw entity ids the rows carry once
 * `dashboardTicketFromNode` has decoded them out of their global ids.
 */
function translateGroupKey(key: string, groupBy: DashboardGroupByToken): string {
	if (groupBy === 'assignee' && key === '') return UNASSIGNED_KEY;
	return key;
}

/**
 * Maps the server's rollup payload onto the shapes the totals row and the group
 * headers already consume.
 *
 * Ratios (`pctResolved`, `slaAchievementRate`) pass through untouched so the
 * formatting layer rounds them once — see `totalsFormatting.ts`. Note that the
 * rollup's resolved counts and SLA rate are derived from ticket status, while the
 * SLA gauge beside them is derived from resolution timestamps; the two select
 * different rows and can legitimately differ on one screen.
 *
 * Returns `{overall, groupStats: null}` when the payload carries no groups (no
 * dimension was requested, or the requested one has none), so callers fall back
 * to client-side stats for every bucket uniformly instead of per bucket.
 */
export function mapRollupsResponse(rollups: RollupsServerPayload, groupBy: DashboardGroupByToken): DashboardRollupsResult {
	const overall = rollupToTotals(rollups.overall);

	if (rollups.groups == null) {
		return {overall, groupStats: null};
	}

	const groupStats: Map<string, TicketGroupStats> = new Map();
	for (const group of rollups.groups) {
		groupStats.set(translateGroupKey(group.key, groupBy), {
			count: group.rollup.total,
			resolvedRatio: group.rollup.pctResolved ?? null,
			medianResolutionSeconds: group.rollup.medianResolutionTimeSeconds ?? null,
		});
	}

	return {overall, groupStats};
}

/**
 * Server-side totals and per-group sub-totals over the whole filtered set, or
 * null while there are none to show.
 *
 * This is a supplementary read, not the boundary that loads the page: the rows,
 * the gauge and the trend each come through their own `ForecastQueryRenderer`,
 * and the totals row has a working client-side computation to fall back on. That
 * fallback is the reason the query is fetched imperatively rather than mounted as
 * a fourth render boundary — a boundary turns any failure into an app-level error
 * modal that reloads the page, and there are two ordinary ways for this read to
 * fail. It is gated on the ticketing admin permission, which the rows are not;
 * and it is the dashboard's heaviest read, deliberately uncapped so it can count
 * a whole account, so it is the first thing to time out on a large one. Neither
 * is a reason to interrupt a dashboard that is otherwise working, so both leave
 * the totals row on `dashboardTotals` instead.
 */
export function useDashboardRollups(
	applied: DashboardFilterState,
	from: string,
	to: string,
	dateField: DashboardDateField,
	groupBy: DashboardGroupByToken
): DashboardRollupsResult | null {
	const rollupGroupBy = rollupGroupByFor(groupBy);
	const input = useMemo(
		() => buildDashboardRollupsInput(applied, from, to, dateField, groupBy),
		// `groupBy` reaches the request only through its rollup dimension, so it is
		// that dimension the request is keyed on: switching between two tokens which
		// both have none ("No grouping" to "Project") must not re-run a whole-set scan
		// to receive the same answer.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[applied, from, to, dateField, rollupGroupBy]
	);

	const [result, setResult] = useState<DashboardRollupsResult | null>(null);

	useEffect(() => {
		let cancelled = false;
		// Cleared before the request rather than after it: a rollup built for a
		// superseded filter selection describes a different set of tickets than the
		// rows now on screen, so it must leave immediately and let the client-side
		// totals hold the row until the replacement lands.
		setResult(null);
		forecastFetchQuery(DashboardRollupsQuery, {rollupInput: input})
			.then(data => {
				if (cancelled) return;
				const payload = (data as useDashboardRollupsQuery$data | null)?.viewer?.ticketDashboardRollups ?? null;
				// `groupBy` is read from the render that queued this fetch, which is
				// safe: the only way it can have changed since is a change that also
				// changed `input` and therefore superseded this fetch, or a change
				// between two tokens with no dimension — and those answer with no
				// groups to key at all.
				setResult(payload != null ? mapRollupsResponse(payload, groupBy) : null);
			})
			.catch(() => {
				if (cancelled) return;
				setResult(null);
			});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [input]);

	return result;
}
